// Lit ReactiveController that maps keyboard shortcuts to UiActions on the bus.
//
// Listens for window-level `keydown` events while connected to the host's
// lifecycle. Matches the event against KEYBOARD_SHORTCUTS (top-down, first
// match wins), skips when focus is in an editable element (FC-3), then builds
// a UiAction — pulling dynamic payload data (e.g. object/delete selection
// snapshot) from the bus at dispatch time, not at construction.
//
// Source: PRD-C T5.3 / TR6. Uses an arrow-field handler (#handler) so `this`
// is statically bound without bind() or inline closures (FC-10).

import type { ReactiveController, ReactiveControllerHost } from 'lit';

import type { UiAction } from '../bus/ui-action.events.js';
import type { WhiteboardBus } from '../bus/whiteboard-bus.js';
import { isEditableTarget } from '../viewport/viewport.controller.js';
import { KEYBOARD_SHORTCUTS, type ShortcutDefinition } from './keyboard-shortcuts.config.js';

export class KeyboardShortcutsController implements ReactiveController {
    readonly #bus: WhiteboardBus;

    /**
     * Bound keydown handler. Arrow-field form keeps `this` stable across
     * addEventListener / removeEventListener calls without a manual bind.
     */
    readonly #handler = (event: KeyboardEvent): void => {
        // FC-3: skip dispatch when focus is in an editable element.
        if (isEditableTarget(event.target)) return;

        const shortcut = this.#findShortcut(event);
        if (!shortcut) return;

        const action = this.#buildAction(shortcut);
        if (!action) return;

        // Stop the browser default for keys that would otherwise act on the
        // page (arrow keys scroll, Backspace navigates back) once we know the
        // shortcut produced a real action for the current selection.
        event.preventDefault();
        this.#bus.uiActions$.next(action);
    };

    constructor(host: ReactiveControllerHost, bus: WhiteboardBus) {
        this.#bus = bus;
        host.addController(this);
    }

    hostConnected(): void {
        window.addEventListener('keydown', this.#handler);
    }

    hostDisconnected(): void {
        window.removeEventListener('keydown', this.#handler);
    }

    /** First matching shortcut (top-down) or undefined. */
    #findShortcut(event: KeyboardEvent): ShortcutDefinition | undefined {
        const eventKey = normalizeKey(event.key);
        const ctrl = event.ctrlKey || event.metaKey;
        const shift = event.shiftKey;

        return KEYBOARD_SHORTCUTS.find(
            (s) =>
                normalizeKey(s.key) === eventKey &&
                (s.ctrl ?? false) === ctrl &&
                (s.shift ?? false) === shift,
        );
    }

    /**
     * Build the UiAction for a matched shortcut. Returns undefined when the
     * action requires runtime context that is not yet available (e.g. paste
     * needs world coordinates from the pointer position — handled outside
     * this controller in P3+).
     */
    #buildAction(shortcut: ShortcutDefinition): UiAction | undefined {
        switch (shortcut.actionType) {
            case 'history/undo':
                return { type: 'history/undo' };
            case 'history/redo':
                return { type: 'history/redo' };
            case 'clipboard/copy':
                return { type: 'clipboard/copy' };
            case 'clipboard/cut':
                return { type: 'clipboard/cut' };
            // Note: there is deliberately no 'clipboard/paste' case. Paste is not
            // a keydown shortcut here — it runs through the native `paste` event
            // (ImagePasteHandler) so screenshots from the system clipboard keep
            // working. See keyboard-shortcuts.config.ts for the rationale.
            case 'selection/clear':
                return { type: 'selection/clear' };
            case 'selection/select-all':
                return { type: 'selection/select-all' };
            case 'object/delete': {
                // Snapshot selection at dispatch time, not at construction.
                const ids = this.#bus.selection$.value;
                if (ids.length === 0) return undefined;
                return { type: 'object/delete', ids };
            }
            case 'object/nudge': {
                const ids = this.#bus.selection$.value;
                if (ids.length === 0) return undefined;
                // Screen-constant step: a nudge always moves the object by the
                // same number of SCREEN pixels regardless of zoom, so the world
                // delta is divided by the current scale (200% → 0.5 world px).
                const scale = this.#bus.viewport$.value.scale || 1;
                const delta = nudgeDelta(shortcut, scale);
                if (!delta) return undefined;
                return { type: 'object/nudge', ids, dx: delta.dx, dy: delta.dy };
            }
            default:
                return undefined;
        }
    }
}

/** Lowercase single-character keys (letters); leave special keys (Delete, Escape, …) untouched. */
function normalizeKey(key: string): string {
    return key.length === 1 ? key.toLowerCase() : key;
}

/** Screen-pixel step for a single nudge: 10 with Shift, otherwise 1. */
const NUDGE_STEP = 1;
const NUDGE_STEP_SHIFT = 10;

/**
 * Translate an arrow-key shortcut into a WORLD-space delta. The step is defined
 * in SCREEN pixels (1, or 10 with Shift) and divided by the current zoom so the
 * on-screen movement stays constant at any zoom (UX request, 2026-06-01): at
 * 200% a 1-screen-px step is 0.5 world px, at 50% it is 2 world px. World coords
 * are floating-point, so fractional deltas are exact (no rounding). Returns null
 * for a non-arrow key so the caller can bail.
 */
function nudgeDelta(shortcut: ShortcutDefinition, scale: number): { dx: number; dy: number } | null {
    const step = (shortcut.shift ? NUDGE_STEP_SHIFT : NUDGE_STEP) / scale;
    switch (shortcut.key) {
        case 'ArrowUp':
            return { dx: 0, dy: -step };
        case 'ArrowDown':
            return { dx: 0, dy: step };
        case 'ArrowLeft':
            return { dx: -step, dy: 0 };
        case 'ArrowRight':
            return { dx: step, dy: 0 };
        default:
            return null;
    }
}
